import { Box, ButtonBase, Checkbox, Drawer, IconButton, Typography } from "@mui/material";
import { Icon } from "@iconify/react";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { HistoryOrderType } from "../../../../services/orders/history";
import { getTypeLabel } from "../helpers";

interface TypeFilterDrawerProps {
  open: boolean;
  value: HistoryOrderType[];
  onClose: () => void;
  onApply: (types: HistoryOrderType[]) => void;
}

interface Section {
  titleKey: string;
  codes: HistoryOrderType[];
}

const SECTIONS: Section[] = [
  {
    titleKey: "history:filters.sections.payments",
    codes: ["CARD_ISSUE", "CARD_TOPUP"],
  },
  {
    titleKey: "history:filters.sections.swaps",
    codes: ["WITHDRAW", "EXCHANGE", "SBP_PAYOUT"],
  },
  {
    titleKey: "history:filters.sections.other",
    codes: ["DEPOSIT", "ADJUSTMENT"],
  },
];

export function TypeFilterDrawer({ open, value, onClose, onApply }: TypeFilterDrawerProps) {
  const { t } = useTranslation(["history", "common"]);
  const [selected, setSelected] = useState<HistoryOrderType[]>(value);

  useEffect(() => {
    if (open) setSelected(value);
  }, [open, value]);

  const allSelected = selected.length === 0;

  const toggle = (code: HistoryOrderType) => {
    setSelected((prev) =>
      prev.includes(code) ? prev.filter((c) => c !== code) : [...prev, code]
    );
  };

  const setAll = () => setSelected([]);

  const apply = () => {
    onApply(selected);
    onClose();
  };

  return (
    <Drawer
      anchor="bottom"
      open={open}
      onClose={onClose}
      sx={{ zIndex: 100010 }}
      slotProps={{ backdrop: { sx: { backgroundColor: "rgba(0, 0, 0, 0.55)" } } }}
      PaperProps={{
        sx: {
          backgroundColor: "secondary.main",
          borderTopLeftRadius: "24px",
          borderTopRightRadius: "24px",
          padding: "10px 16px 20px",
          backgroundImage: "none",
          maxHeight: "88vh",
          display: "flex",
          flexDirection: "column",
        },
      }}
    >
      <Box
        sx={(theme) => ({
          width: 44,
          height: 4,
          borderRadius: 4,
          bgcolor:
            theme.palette.mode === "dark"
              ? "rgba(255, 255, 255, 0.2)"
              : "rgba(0, 0, 0, 0.2)",
          margin: "0 auto 14px",
        })}
      />

      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          marginBottom: "14px",
        }}
      >
        <Typography sx={{ fontSize: "20px", fontWeight: 700 }}>
          {t("history:filters.type_drawer_title")}
        </Typography>
        <IconButton onClick={onClose} sx={{ padding: "6px" }}>
          <Icon icon="solar:close-circle-bold-duotone" width={26} height={26} />
        </IconButton>
      </Box>

      <Box sx={{ flex: 1, overflowY: "auto", display: "grid", gap: "14px" }}>
        <SelectAllRow checked={allSelected} onClick={setAll} />

        {SECTIONS.map((section) => (
          <Box key={section.titleKey}>
            <Typography
              sx={{
                fontSize: "11px",
                fontWeight: 600,
                color: "text.secondary",
                textTransform: "uppercase",
                letterSpacing: "0.6px",
                marginBottom: "8px",
                paddingInline: "4px",
              }}
            >
              {t(section.titleKey)}
            </Typography>
            <Box
              sx={(theme) => ({
                borderRadius: "18px",
                overflow: "hidden",
                bgcolor:
                  theme.palette.mode === "dark"
                    ? "rgba(255, 255, 255, 0.04)"
                    : "rgba(0, 0, 0, 0.04)",
              })}
            >
              {section.codes.map((code, i) => (
                <FilterRow
                  key={code}
                  label={getTypeLabel(code, t)}
                  checked={selected.includes(code)}
                  onClick={() => toggle(code)}
                  divider={i < section.codes.length - 1}
                />
              ))}
            </Box>
          </Box>
        ))}
      </Box>

      <ButtonBase
        onClick={apply}
        sx={{
          marginTop: "16px",
          padding: "16px",
          borderRadius: "16px",
          bgcolor: "primary.main",
          color: "primary.contrastText",
          fontWeight: 700,
          fontSize: "15px",
          transition: "opacity 0.15s",
          "&:active": { opacity: 0.85 },
        }}
      >
        {t("common:actions.apply")}
      </ButtonBase>
    </Drawer>
  );
}

function SelectAllRow({ checked, onClick }: { checked: boolean; onClick: () => void }) {
  const { t } = useTranslation("history");
  return (
    <ButtonBase
      onClick={onClick}
      sx={(theme) => ({
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        width: "100%",
        padding: "14px 16px",
        borderRadius: "18px",
        bgcolor:
          theme.palette.mode === "dark"
            ? "rgba(255, 255, 255, 0.04)"
            : "rgba(0, 0, 0, 0.04)",
      })}
    >
      <Typography sx={{ fontSize: "15px", fontWeight: 600 }}>
        {t("filters.select_all_types")}
      </Typography>
      <Checkbox
        checked={checked}
        onClick={(e) => e.stopPropagation()}
        onChange={onClick}
        sx={{ padding: 0, color: "text.secondary" }}
      />
    </ButtonBase>
  );
}

function FilterRow({
  label,
  hint,
  checked,
  onClick,
  divider,
}: {
  label: string;
  hint?: string;
  checked: boolean;
  onClick: () => void;
  divider: boolean;
}) {
  return (
    <ButtonBase
      onClick={onClick}
      sx={(theme) => ({
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        width: "100%",
        padding: "14px 16px",
        textAlign: "left",
        borderBottom: divider
          ? `1px solid ${
              theme.palette.mode === "dark"
                ? "rgba(255, 255, 255, 0.05)"
                : "rgba(0, 0, 0, 0.05)"
            }`
          : "none",
      })}
    >
      <Box sx={{ display: "flex", alignItems: "baseline", gap: "6px" }}>
        <Typography sx={{ fontSize: "15px", fontWeight: 500 }}>{label}</Typography>
        {hint && (
          <Typography sx={{ fontSize: "13px", color: "text.secondary" }}>
            {hint}
          </Typography>
        )}
      </Box>
      <Checkbox
        checked={checked}
        onClick={(e) => e.stopPropagation()}
        onChange={onClick}
        sx={{ padding: 0, color: "text.secondary" }}
      />
    </ButtonBase>
  );
}
